import React, {useCallback, useEffect, useState} from 'react'
import {RouteComponentProps, Link} from '@reach/router'
import {CartXFill} from 'react-bootstrap-icons'
import {useDebounce} from 'use-debounce'

import {Page, Container, Loader} from '../layout'
import {ensureList} from '../../utils/typing'
import {formatCurrency} from '../../utils/numbers'
import {useCart} from '../../contexts/CartContext'
import {MyCheckout_me_checkout_lines} from '../../contexts/_gql-types/MyCheckout'
import {useApi} from '../../api/rest'
import {ShippingMethod} from '../../api/restTypes'
import RatioImage from '../common/RatioImage'
import CheckoutForm from './CheckoutForm'


interface CartLineProps {
  line: MyCheckout_me_checkout_lines
}

function CartLine({line}: CartLineProps) {
  const {cartUpdate} = useCart()
  const [quantity, setQuantity] = useState(line.quantity)
  const [debouncedQuantity] = useDebounce(quantity, 500)

  const {variant} = line
  const {product} = variant
  const {thumbnail, thumbnail2x} = product

  const totalPrice = formatCurrency(line.totalPrice?.net.amount)
  const price = formatCurrency(variant.pricing?.price?.net.amount)

  const submitChanges = useCallback(() => {
    cartUpdate({[variant.id]: debouncedQuantity})
  }, [debouncedQuantity, variant.id, cartUpdate])

  const onQtyChange = useCallback<(e: React.ChangeEvent<HTMLInputElement>) => void>((e) => {
    const {value} = e.target

    setQuantity(Number(value))
  }, [setQuantity])

  useEffect(() => {
    if (quantity === debouncedQuantity) {
      submitChanges()
    }
  }, [quantity, debouncedQuantity, submitChanges])

  return (
    <div className="card-hover-wrapper">
      <div className="card border-0 mb-3 rounded bg-white px-3 py-4 py-md-3">
        <div className="row flex-md-row flex-column">
          <Link className="col text-reset" to={`/verslun/vorur/${product.slug}/`}>
            <div className="row">
              <div className="col-4 col-lg-2" style={{minWidth: 150}}>
                <RatioImage
                  src={thumbnail?.url}
                  src2x={thumbnail2x?.url}
                  ratio={[4, 3]}
                  style={{minHeight: '100%'}}
                />
              </div>

              <div className="col d-flex flex-column justify-content-center">
                <h5 className="">{product.name}</h5>
                <p className="mb-0 text-muted d-md-block d-flex flex-column">
                  <small>Vörunúmer: <strong>{variant.sku}</strong></small>
                  <small className="pl-md-3">Einingarverð: <strong>{price} kr.</strong></small>
                </p>
              </div>
            </div>
          </Link>

          <div className="col pl-2">
            <div className="col d-flex align-items-center justify-content-end pt-4 pt-md-0 h-100">
              <div className="d-flex align-items-center justify-content-end mb-0 pr-4">
                <span className="text-muted pr-2">Magn:</span>
                <input
                  id={product.id}
                  name={variant.id}
                  type="number"
                  value={quantity}
                  className="form-control"
                  style={{width: 80}}
                  min={0}
                  onChange={onQtyChange}
                />
              </div>
              <p className="mb-0"><span className="text-muted">Verð:</span> <strong>{totalPrice} kr.</strong></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Cart(_: RouteComponentProps) {
  const {cart, cartLoading, shippingPrice} = useCart()

  const shippingMethodsRes = useApi<ShippingMethod[]>('/shipping-methods/')
  const shippingMethods = ensureList(shippingMethodsRes.data)

  const freeShippingMethod = shippingMethods.find((m) => !m.price_amount && m.minimum_order_price_amount)
  const freeShippingMinPrice = formatCurrency(freeShippingMethod?.minimum_order_price_amount)

  if (cartLoading) {
    return <Page><Loader /></Page>
  }

  const netSubtotal = cart?.subtotalPrice?.net.amount
  const grossTotal = cart?.totalPrice?.gross.amount || 0
  const netTotal = cart?.totalPrice?.net.amount || 0
  const totalVAT = grossTotal - netTotal

  return (
    <Page>
      <Container size="lg">
        {cart ? (
          <div className="py-5">
            <h1 className="mb-5">Karfa</h1>

            <div className="row">
              {ensureList(cart.lines).map((line) => (
                <div key={line.id} className="col-12">
                  <CartLine line={line} />
                </div>
              ))}
            </div>

            <div className="mb-5">
              <h5 className="mb-0 mr-3 mt-3 d-flex justify-content-end">
                Heildarverð: <strong className="pl-3">{formatCurrency(netSubtotal)} kr.</strong>
              </h5>
              <h6 className="mb-0 mr-3 mt-3 d-flex justify-content-end">
                Sendingarkostnaður: <strong className="pl-3">+ {formatCurrency(shippingPrice || 0)} kr.</strong>
              </h6>
              <h6 className="mb-0 mr-3 mt-3 d-flex justify-content-end">
                VSK: <strong className="pl-3">+ {formatCurrency(totalVAT)} kr.</strong>
              </h6>
              {!!(shippingPrice && freeShippingMethod) && (
                <div className="mb-0 mr-3 mt-1 d-flex justify-content-end text-muted">
                  Verslaðu fyrir {freeShippingMinPrice} kr. eða meira og fáðu fría sendingu.
                </div>
              )}
            </div>
            {<h5 className="mb-0 mr-3 mt-3 d-flex justify-content-end">
                Samtals: <strong className="pl-3">{formatCurrency(grossTotal)} kr.</strong>
              </h5>}
            <h2 className="mb-5">Afhending</h2>
            <CheckoutForm />
          </div>
        ) : (
          <div className="py-5 d-flex flex-column align-items-center justify-content-center">
            <CartXFill size={40} className="text-muted mb-3" />
            <h5 className="text-muted">Karfan þín er tóm</h5>
          </div>
        )}
      </Container>
    </Page>
  )
}
